import { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Brand } from "./Brand";
import { HelpDialog } from "./HelpDialog";
import { useSession } from "../session";

interface Item {
  name: string;
  icon: string;
  path: string;
  visible: (session: ReturnType<typeof useSession>) => boolean;
}

const items: Item[] = [
  { name: "Leads", icon: "fa-inbox", path: "/", visible: () => true },
  {
    name: "Delivery",
    icon: "fa-paper-plane",
    path: "/delivery",
    visible: (session) => session.canEditConfig,
  },
  {
    name: "Team",
    icon: "fa-user-group",
    path: "/team",
    visible: (session) => Boolean(session.role && ["owner", "manager"].includes(session.role)),
  },
  {
    name: "Settings",
    icon: "fa-gear",
    path: "/settings",
    visible: (session) => session.role === "owner",
  },
];

export function Sidebar() {
  const session = useSession();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const closeHelp = () => {
    setHelpOpen(false);
    window.requestAnimationFrame(() => helpButtonRef.current?.focus());
  };

  return (
    <aside className="w-[268px] shrink-0 h-screen bg-surface-white border-r border-default flex flex-col">
      <div className="px-[25px] pt-[25px] pb-[20px] shrink-0">
        <Brand />
      </div>

      <nav className="flex-1 overflow-y-auto scroll-thin px-[15px] pt-2">
        <div className="flex flex-col gap-0.5">
          {items
            .filter((item) => item.visible(session))
            .map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-[9px] rounded-xs transition-colors",
                    isActive
                      ? "bg-brand-red-tint text-brand-red"
                      : "text-nav hover:bg-surface-light",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <i
                      className={`fa-solid ${item.icon} text-[16px] w-[18px] text-center ${
                        isActive ? "text-brand-red" : "text-nav"
                      }`}
                    />
                    <span className="text-body font-semibold">{item.name}</span>
                  </>
                )}
              </NavLink>
            ))}
        </div>
      </nav>

      <div className="shrink-0 border-t border-default px-[15px] pt-3 pb-4 flex flex-col gap-0.5">
        <button
          ref={helpButtonRef}
          type="button"
          onClick={() => setHelpOpen(true)}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xs text-nav hover:bg-surface-light transition-colors w-full"
        >
          <i className="fa-solid fa-circle-question text-[16px] w-[18px] text-center" />
          <span className="text-body font-semibold">Help</span>
        </button>
        <button
          onClick={session.exit}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xs text-nav hover:bg-surface-light transition-colors w-full"
        >
          <i className="fa-solid fa-right-from-bracket text-[16px] w-[18px] text-center" />
          <span className="text-body font-semibold">Log out</span>
        </button>
      </div>
      {helpOpen && <HelpDialog onClose={closeHelp} />}
    </aside>
  );
}
